import { useState, useEffect, useMemo } from 'react'; // Keep useCallback if used elsewhere
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
// Import Controller from react-hook-form
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
// Import react-select
import Select, { GroupBase, OptionsOrGroups } from 'react-select';
// Import useNavigate for React Router navigation
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useGlobalQueryStore } from '../store/globalQueryStore';

// SECURITY: Import user-aware cache utilities for secure cache isolation
import { useUserContext } from '../hooks/useUserContext';
import { UserAwareCacheKeys } from '../utils/userAwareCacheKeys';
import { PortfolioConfigCards } from '../components/portfolio/PortfolioConfigCards';
import {
  UserIcon,
  CreditCardIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon as EyeIconOutline,
  ServerIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  ChevronDownIcon,
  EyeSlashIcon,
  MinusIcon, // Import MinusIcon
  ListBulletIcon, // Import ListBulletIcon
  XMarkIcon, // Import XMarkIcon for cancel button
  ArrowTopRightOnSquareIcon, // Add ArrowTopRightOnSquareIcon
  EnvelopeIcon, // Add EnvelopeIcon
  CogIcon,
  ShieldCheckIcon, // Add ShieldCheckIcon for Privacy section
  ExclamationTriangleIcon, // Add ExclamationTriangleIcon for timezone confirmation
} from '@heroicons/react/24/outline';
import {
  // API Functions
  fetchUserSettings,
  updateUserSettings,
  fetchSubscription,
  fetchLinkedAccounts,
  createLinkedAccount,
  updateLinkedAccount,
  deleteLinkedAccount,
  createCustomerPortalSession, // Add createCustomerPortalSession
  apiClient, // Add apiClient for trade sync
  confirmTimezone, // Add confirmTimezone function
  // Portfolio Config API functions
  getPortfolioConfigCategories,
  updatePortfolioConfig,
  resetPortfolioCategoryToDefaults,
  deletePortfolioConfigOverride,
  // Types
  UserSettingsResponse,
  UserSettingsUpdate,
  SubscriptionResponse,
  UserAccountResponse,
  UserAccountCreate,
  UserAccountUpdate,
  // Portfolio Config types
  PortfolioConfigCategories,
  UpdatePortfolioConfigRequest,
} from '../services/api';
// Import date-fns functions
//import { format, parseISO } from 'date-fns'; // Removed unused date-fns functions
// Import custom date formatting utils
import { formatUserDateTime, DATE_FORMAT_SHORT } from '../utils/formatDate';

// Import consent manager
import { consentManager } from '../services/consentManager';

// --- Import Sync Components ---
import TradeSyncButton from '../components/TradeSyncButton';
import SyncStatusIndicator from '../components/SyncStatusIndicator';
// --- Import Theme Toggle Component ---
// import ThemeToggle from '../components/ThemeToggle'; // Removed ThemeToggle import
// --- Import Trading Reports Section ---
import { TradingReportsSection } from '../components/reports/TradingReportsSection';

// --- Curated Timezone List ---
const commonTimezones = [
  'UTC',
  'America/New_York',    // US East Coast
  'America/Chicago',     // US Central
  'America/Denver',      // US Mountain
  'America/Los_Angeles', // US West Coast
  'Europe/London',
  'Europe/Berlin',       // Central European Time
  'Asia/Tokyo',
  'Asia/Shanghai',       // China Standard Time
  'Asia/Dubai',
  'Australia/Sydney',
];

// --- Form Types ---

type ProfileBillingInputs = UserSettingsUpdate;

type LinkedAccountInputs = UserAccountCreate & { id?: number; exchange_name?: string; passphrase?: string }; // Add exchange_name and passphrase

// Portfolio Config Form Types - Updated to allow nested objects
type PortfolioConfigInputs = {
  [key: string]: any;
};

// --- Helper Components ---

// Cookie Preferences, Email, and Privacy Section Component - Collapsible
const CookiePreferencesSection = () => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
    personalization: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Load current preferences
    const currentConsent = consentManager.getConsent();
    if (currentConsent) {
      setPreferences({
        analytics: currentConsent.analytics,
        marketing: currentConsent.marketing,
        personalization: currentConsent.personalization
      });
    }
    setIsLoading(false);
  }, []);

  const handleUpdatePreferences = async () => {
    setIsUpdating(true);
    try {
      await consentManager.updateConsent(preferences, 'settings');
      toast.success('Cookie preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update cookie preferences');
    } finally {
      setIsUpdating(false);
    }
  };

  const togglePreference = (key: keyof typeof preferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="border-t border-white/10">
      {/* Collapsible Header */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-4 flex justify-between items-center cursor-pointer bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-colors duration-200 rounded-t-lg"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center space-x-3">
          <ShieldCheckIcon className="h-5 w-5 text-blue-400" />
          <div>
            <h3 className="text-base font-semibold text-white/90">Privacy & Communication</h3>
            <p className="text-xs text-white/60 mt-0.5">Cookie preferences, email settings, and data management</p>
          </div>
        </div>
        {isExpanded ? <ChevronUpIcon className="h-5 w-5 text-white/70" /> : <ChevronDownIcon className="h-5 w-5 text-white/70" />}
      </div>

      {/* Collapsible Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="cookie-preferences-content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: 'auto', overflow: 'hidden' },
              collapsed: { opacity: 0, height: 0, overflow: 'hidden' }
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="px-4 pb-4 bg-white/5 backdrop-blur-sm space-y-6 rounded-b-lg border-t border-white/10">
              {isLoading ? (
                <div className="space-y-4">
                  <div className="h-6 bg-white/10 rounded animate-pulse"></div>
                  <div className="h-4 bg-white/10 rounded animate-pulse w-3/4"></div>
                  <div className="h-4 bg-white/10 rounded animate-pulse w-1/2"></div>
                </div>
              ) : (
                <>
                  {/* Cookie Preferences */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-semibold text-white/90 mb-2">Cookie Preferences</h4>
                      <p className="text-sm text-white/70 mb-4">
                        Manage your cookie and data processing preferences. Changes take effect immediately.
                      </p>
                    </div>

                    {/* Essential Cookies */}
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg border border-white/20">
                      <div>
                        <h5 className="text-white font-medium">Essential Cookies</h5>
                        <p className="text-white/60 text-sm mt-1">
                          Required for basic functionality, authentication, and security
                        </p>
                      </div>
                      <div className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                        Always Active
                      </div>
                    </div>

                    {/* Analytics Toggle */}
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg border border-white/20">
                      <div>
                        <h5 className="text-white font-medium">Analytics & Performance</h5>
                        <p className="text-white/60 text-sm mt-1">
                          Help us understand usage patterns and improve app performance via Sentry
                        </p>
                      </div>
                      <button
                        onClick={() => togglePreference('analytics')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          preferences.analytics ? 'bg-blue-600' : 'bg-white/20'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                            preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Marketing Toggle */}
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg border border-white/20">
                      <div>
                        <h5 className="text-white font-medium">Marketing & Communication</h5>
                        <p className="text-white/60 text-sm mt-1">
                          Enable personalized content and relevant feature notifications via Omnisend
                        </p>
                      </div>
                      <button
                        onClick={() => togglePreference('marketing')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          preferences.marketing ? 'bg-blue-600' : 'bg-white/20'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                            preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Personalization Toggle */}
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg border border-white/20">
                      <div>
                        <h5 className="text-white font-medium">Personalization & Features</h5>
                        <p className="text-white/60 text-sm mt-1">
                          Customize your experience and enable advanced trading features via A/B testing
                        </p>
                      </div>
                      <button
                        onClick={() => togglePreference('personalization')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences.personalization ? 'bg-blue-600' : 'bg-white/20'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.personalization ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={handleUpdatePreferences}
                        disabled={isUpdating}
                        className="px-4 py-2 text-sm rounded-md font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
                      >
                        {isUpdating ? 'Updating...' : 'Save Cookie Preferences'}
                      </button>
                    </div>
                  </div>

                  {/* Detailed Email Preferences Link */}
                  <div className="pt-4 border-t border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-white/90">Detailed Email Preferences</h4>
                        <p className="text-xs text-white/70">Customize specific email types, timing, and frequency</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/email-preferences');
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-300 bg-blue-600/20 border border-blue-500/30 rounded-lg hover:bg-blue-600/30 hover:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-400/50 transition-all duration-200"
                      >
                        <EnvelopeIcon className="w-4 h-4 mr-1.5" />
                        Manage
                        <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1.5" />
                      </button>
                    </div>
                  </div>

                  {/* Privacy & Data Management Link */}
                  <div className="pt-4 border-t border-white/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-white/90">Privacy & Data Management</h4>
                        <p className="text-xs text-white/70">Manage data privacy, export requests, and GDPR compliance</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/privacy-policy#gdpr-controls');
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-300 bg-purple-600/20 border border-purple-500/30 rounded-lg hover:bg-purple-600/30 hover:border-purple-500/50 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all duration-200"
                      >
                        <ShieldCheckIcon className="w-4 h-4 mr-1.5" />
                        Manage
                        <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1.5" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Basic Modal Component
const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; maxWidth?: string }> = ({ isOpen, onClose, title, children, maxWidth = "max-w-md" }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className={`bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-xl p-6 w-full ${maxWidth} mx-4`}
      >
        <div className="flex justify-between items-center mb-4 border-b border-white/20 pb-2">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white cursor-pointer">&times;</button>
        </div>
        {children}
      </motion.div>
    </div>
  );
};

// Simple Input Component for Forms
const FormInput: React.FC<any> = ({ label, id, register, errors, type = 'text', required = false, ...rest }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-white/90 mb-1">{label}</label>
    <input
      id={id}
      type={type}
      {...register(id, { required: required && `${label} is required` })}
      className={`w-full px-3 py-2 border ${errors[id] ? 'border-red-400' : 'border-white/20'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white/10 backdrop-blur-sm text-white placeholder-white/70`}
      {...rest}
    />
    {errors[id] && <p className="mt-1 text-xs text-red-400">{errors[id].message}</p>}
  </div>
);

// Skeleton Loader Component
const SkeletonLoader: React.FC<{ className?: string }> = ({ className = "h-6 bg-white/20 rounded" }) => (
    <div className={`animate-pulse ${className}`}></div>
);

// Helper function for comparing primitive values, accounting for potential type differences
const arePrimValuesDifferent = (cv: any, dv: any): boolean => {
  if (cv === undefined && dv === undefined) return false;
  if (cv === undefined || dv === undefined) return true; // One is undefined, the other is not

  const typeCv = typeof cv;
  const typeDv = typeof dv;

  // If types are the same, direct comparison
  if (typeCv === typeDv) {
    return cv !== dv;
  }

  // Handle number/string comparison
  if ((typeCv === 'number' && typeDv === 'string') || (typeCv === 'string' && typeDv === 'number')) {
    const numCv = parseFloat(String(cv));
    const numDv = parseFloat(String(dv));
    if (!isNaN(numCv) && !isNaN(numDv)) {
      return numCv !== numDv;
    }
    // Fallback if parsing results in NaN for one or both (e.g. "text" vs 123)
    return String(cv) !== String(dv);
  }

  // Handle boolean/string comparison
  if (typeCv === 'boolean' && typeDv === 'string') {
    return cv !== (dv.toLowerCase() === 'true');
  }
  if (typeCv === 'string' && typeDv === 'boolean') {
    return (cv.toLowerCase() === 'true') !== dv;
  }
  
  // For any other mixed types or unhandled cases, fallback to string comparison or consider them different
  // Using JSON.stringify is a robust general fallback for primitives here if direct comparison isn't enough
  return JSON.stringify(cv) !== JSON.stringify(dv);
};

// Validation utilities - comprehensive interface matching backend logic
interface ValidationRule {
  type?: 'number' | 'percent' | 'string' | 'boolean' | 'object' | 'array';
  min?: number;
  max?: number;
  min_length?: number;
  max_length?: number;
  required_fields?: string[];
  properties?: Record<string, ValidationRule>;
  enum?: any[];
  pattern?: string;
  total_must_equal?: number;
  tolerance?: number;
}

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// Field metadata interface
interface FieldMetadata {
  description?: string | null;
  validation_rules?: ValidationRule | null;
  has_user_override?: boolean;
  user_config_name?: string | null;
}

// Comprehensive validation function matching backend portfolio_config_service.py logic
const validateFieldValue = (value: any, rules?: ValidationRule | null, allowPartialInput = false): ValidationResult => {
  if (!rules) return { isValid: true };

  try {
    // For partial input during typing, be more lenient
    if (allowPartialInput && typeof value === 'string') {
      // Allow empty strings and partial numbers during typing
      if (value === '' || value === '0' || value === '0.' || /^\d*\.?\d*$/.test(value)) {
        return { isValid: true };
      }
    }

    // Type validation - exact match to backend logic
    if (rules.type) {
      const expectedType = rules.type;
      
      if (expectedType === "number") {
        // Accept both number and string that can be parsed as number
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (typeof numValue !== "number" || isNaN(numValue)) {
          return { isValid: false, error: "Value must be a number" };
        }
      } else if (expectedType === "percent") {
        // Accept both number and string that can be parsed as number for percentages
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (typeof numValue !== "number" || isNaN(numValue)) {
          return { isValid: false, error: "Value must be a percentage (number between 0 and 100)" };
        }
      } else if (expectedType === "string" && typeof value !== "string") {
        return { isValid: false, error: "Value must be a string" };
      } else if (expectedType === "boolean" && typeof value !== "boolean") {
        return { isValid: false, error: "Value must be a boolean" };
      } else if (expectedType === "object" && (typeof value !== "object" || value === null || Array.isArray(value))) {
        return { isValid: false, error: "Value must be an object" };
      } else if (expectedType === "array" && !Array.isArray(value)) {
        return { isValid: false, error: "Value must be an array" };
      }
    }

    // Range validation for numbers and percentages - FIXED for percent fields
    if (typeof value === "number" || (typeof value === "string" && !isNaN(parseFloat(value)))) {
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      
      // For percent type, validate against whole number range (0-100)
      if (rules.type === "percent") {
        const minVal = rules.min !== undefined ? rules.min : 0;
        const maxVal = rules.max !== undefined ? rules.max : 100;
        
        if (numValue < minVal) {
          return { isValid: false, error: `Percentage must be at least ${minVal}%` };
        }
        if (numValue > maxVal) {
          return { isValid: false, error: `Percentage must be at most ${maxVal}%` };
        }
      } else {
        // Regular number validation
        if (rules.min !== undefined && numValue < rules.min) {
          return { isValid: false, error: `Value must be at least ${rules.min}` };
        }
        if (rules.max !== undefined && numValue > rules.max) {
          return { isValid: false, error: `Value must be at most ${rules.max}` };
        }
      }
    }

    // Length validation for strings and arrays - matches backend logic
    if (typeof value === "string" || Array.isArray(value)) {
      if (rules.min_length !== undefined && value.length < rules.min_length) {
        return { isValid: false, error: `Length must be at least ${rules.min_length}` };
      }
      if (rules.max_length !== undefined && value.length > rules.max_length) {
        return { isValid: false, error: `Length must be at most ${rules.max_length}` };
      }
    }

    // Required fields validation for objects - matches backend logic
    if (typeof value === "object" && value !== null && !Array.isArray(value) && rules.required_fields) {
      const missingFields = rules.required_fields.filter(field => !(field in value));
      if (missingFields.length > 0) {
        return { isValid: false, error: `Missing required fields: ${missingFields.join(", ")}` };
      }
    }

    // Enum validation - matches backend logic
    if (rules.enum && rules.enum.length > 0 && !rules.enum.includes(value)) {
      return { isValid: false, error: `Value must be one of: ${rules.enum.join(", ")}` };
    }

    // Pattern validation for strings - matches backend logic
    if (rules.pattern && typeof value === "string") {
      try {
        const regex = new RegExp(rules.pattern);
        if (!regex.test(value)) {
          return { isValid: false, error: "Value does not match required pattern" };
        }
      } catch (regexError) {
        return { isValid: false, error: "Invalid validation pattern" };
      }
    }

    // Total sum validation for objects - matches backend logic exactly
    if (typeof value === "object" && value !== null && !Array.isArray(value) && rules.total_must_equal !== undefined) {
      const expectedTotal = rules.total_must_equal;
      const actualTotal = Object.values(value).reduce((sum: number, v) => {
        if (typeof v === "number") {
          return sum + v;
        } else if (typeof v === "string") {
          const numValue = parseFloat(v);
          return sum + (isNaN(numValue) ? 0 : numValue);
        }
        return sum;
      }, 0);
      const tolerance = rules.tolerance !== undefined ? rules.tolerance : 0.01;

      if (Math.abs(actualTotal - expectedTotal) > tolerance) {
        return { isValid: false, error: `Values must sum to ${expectedTotal}, but sum is ${actualTotal.toFixed(2)}` };
      }
    }

    return { isValid: true };
  } catch (error) {
    return { isValid: false, error: `Validation error: ${error}` };
  }
};

// Recursive Portfolio Config Field Component
interface PortfolioConfigFieldProps {
  fieldKey: string;
  fieldPath: string;
  currentValue: any;
  defaultValue: any;
  hasUserOverride: boolean;
  metadata?: FieldMetadata;
  depth?: number;
  onValueChange: (path: string, value: any) => void;
  onRemoveOverride: (path: string) => void;
  register: any;
  watchFunction: (path: string, defaultValue?: any) => any;
  setFormValue: (path: string, value: any) => void;
}

const PortfolioConfigField: React.FC<PortfolioConfigFieldProps> = ({
  fieldKey,
  fieldPath,
  currentValue,
  defaultValue,
  hasUserOverride,
  metadata,
  depth = 0,
  onValueChange,
  onRemoveOverride,
  register,
  watchFunction,
  setFormValue,
}) => {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pendingSliderValue, setPendingSliderValue] = useState<number | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingValue, setTypingValue] = useState<string>('');
  const rules = metadata?.validation_rules;
  
  // Phase 2: Simplified percent detection - all percentages are now whole numbers
  const isPercent = rules?.type === 'percent';
  
  // Determine if the field is actually modified, considering types for primitives
  let isActuallyModified: boolean;
  if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue) && 
      typeof defaultValue === 'object' && defaultValue !== null && !Array.isArray(defaultValue)) {
    // For objects, compare their stringified versions.
    isActuallyModified = JSON.stringify(currentValue) !== JSON.stringify(defaultValue);
  } else {
    // For primitives (or if one is object and other is not, which implies modification), use type-aware comparison.
    isActuallyModified = arePrimValuesDifferent(currentValue, defaultValue);
  }
  const isModified = hasUserOverride && isActuallyModified;

  const indentClass = depth > 0 ? `ml-${Math.min(depth * 4, 16)} pl-4 border-l-2 border-white/20` : '';  const watchedValue = watchFunction(fieldPath, currentValue);
  
  // For nested objects, calculate totals if total_must_equal is specified
  const calculateCurrentTotal = () => {
    if (!rules?.total_must_equal) {
      return null;
    }
    
    // Use watchedValue (current form state) instead of static currentValue
    const valueToCheck = watchedValue || currentValue;
    
    if (typeof valueToCheck !== 'object' || valueToCheck === null) {
      return null;
    }
    
    return Object.values(valueToCheck).reduce((sum: number, v) => {
      // Handle both numbers and string numbers (from form inputs)
      if (typeof v === "number") {
        return sum + v;
      } else if (typeof v === "string") {
        const numValue = parseFloat(v);
        return sum + (isNaN(numValue) ? 0 : numValue);
      }
      return sum;
    }, 0);
  };

  const currentTotal = calculateCurrentTotal();
  const expectedTotal = rules?.total_must_equal;
  const tolerance = rules?.tolerance ?? 0.01;
  const isTotalValid = currentTotal === null || Math.abs(currentTotal - (expectedTotal || 0)) <= tolerance;

  // For nested objects, render recursively
  if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue)) {
    return (
      <div className={`space-y-4 ${indentClass}`}>
        <div className="flex justify-between items-start">
          <div className="flex-1">            <h4 className={`text-sm font-medium text-white/90 capitalize mb-2 ${depth > 0 ? 'text-xs' : ''}`}>
              {fieldKey.replace(/_/g, ' ')}
            </h4>
            {metadata?.description && (
              <p className="text-xs text-white/70 mb-2">{metadata.description}</p>
            )}
            <div className="flex items-center space-x-4 text-xs text-white/60">
              {hasUserOverride && <span className="text-blue-400">(Custom)</span>}
              {isModified && <span className="text-orange-400 font-medium">Modified</span>}
              {!hasUserOverride && <span className="text-white/50">Using Default</span>}
            </div>{/* Enhanced total validation display - always reserve space to prevent layout shifts */}
            <div className={`mt-3 transition-all duration-300 ${
              expectedTotal && currentTotal !== null 
                ? 'opacity-100' 
                : 'opacity-0 h-0 overflow-hidden'
            }`}>
              {expectedTotal && currentTotal !== null && (
                <div className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                  isTotalValid 
                    ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20' 
                    : 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                }`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-sm font-medium ${
                      isTotalValid 
                        ? 'text-green-800 dark:text-green-200' 
                        : 'text-red-800 dark:text-red-200'
                    }`}>
                      Sum Validation
                    </span>
                    <span className={`text-sm font-bold ${
                      isTotalValid 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {currentTotal.toFixed(2)} / {expectedTotal}
                    </span>
                  </div>
                  
                  {/* Progress bar showing current total vs expected */}
                  <div className="w-full bg-white/20 backdrop-blur-sm rounded-full h-2 mb-2 border border-white/10">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        isTotalValid 
                          ? 'bg-green-500 dark:bg-green-400' 
                          : currentTotal > expectedTotal 
                            ? 'bg-red-500 dark:bg-red-400' 
                            : 'bg-yellow-500 dark:bg-yellow-400'
                      }`}
                      style={{ 
                        width: `${Math.min((currentTotal / expectedTotal) * 100, 100)}%`
                      }}
                    />
                  </div>
                  
                  <div className={`flex items-center justify-between text-xs ${
                    isTotalValid 
                      ? 'text-green-700 dark:text-green-300' 
                      : 'text-red-700 dark:text-red-300'
                  }`}>
                    <span>
                      {isTotalValid ? '✅ Valid total' : currentTotal > expectedTotal ? '⚠️ Exceeds target' : '⚠️ Below target'}
                    </span>
                    <span className="font-medium">
                      {expectedTotal - currentTotal > 0 
                        ? `${(expectedTotal - currentTotal).toFixed(2)} remaining` 
                        : currentTotal > expectedTotal 
                          ? `${(currentTotal - expectedTotal).toFixed(2)} over` 
                          : 'Perfect!'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          {hasUserOverride && (
            <button
              type="button"
              onClick={() => onRemoveOverride(fieldPath)}
              className="ml-2 px-2 py-1 text-xs border border-white/30 rounded text-white/70 hover:bg-white/10 hover:text-red-400 transition-all duration-200 backdrop-blur-sm"
              title="Revert to default"
            >
              Reset
            </button>
          )}
        </div>
        
        <div className="space-y-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg border border-white/10">
          {Object.entries(currentValue).map(([nestedKey, nestedValue]) => {
            const nestedPath = `${fieldPath}.${nestedKey}`;
            const nestedDefaultValue = defaultValue?.[nestedKey];
            const nestedHasOverride = hasUserOverride; // If parent has override, all children do too
            
            // Extract nested field metadata from validation rules
            const nestedMetadata: FieldMetadata = {
              validation_rules: rules?.properties?.[nestedKey],
              description: null, // Object properties typically don't have individual descriptions
            };
              return (
              <PortfolioConfigField
                key={nestedKey}
                fieldKey={nestedKey}
                fieldPath={nestedPath}
                currentValue={nestedValue}
                defaultValue={nestedDefaultValue}
                hasUserOverride={nestedHasOverride}
                metadata={nestedMetadata}
                depth={depth + 1}
                onValueChange={onValueChange}
                onRemoveOverride={onRemoveOverride}
                register={register}
                watchFunction={watchFunction}
                setFormValue={setFormValue}
              />
            );
          })}
        </div>
      </div>
    );
  }  // Handle value change with validation
  const handleValueChange = (newValue: any, isSliderChange = false, skipValidation = false) => {
    console.log('📝 PortfolioConfigField handleValueChange called:', {
      fieldPath,
      newValue,
      isSliderChange,
      skipValidation
    });
    
    // Phase 2: Simplified - no percentage conversion needed, store values directly
    let valueToStore = newValue;
    
    // Always validate to show errors, but don't block saving unless skipValidation is true
    if (!skipValidation) {
      const validation = validateFieldValue(valueToStore, rules);
      if (!validation.isValid) {
        setValidationError(validation.error || 'Invalid value');
        // Continue to save the value even if validation fails
        // This allows user to see their input and the error message
      } else {
        setValidationError(null);
      }
    } else {
      setValidationError(null);
    }
    
    // For slider changes, store the pending value but don't trigger API call yet
    if (isSliderChange) {
      setPendingSliderValue(valueToStore);
      return;
    }
    
    // Always save the value, even if validation fails
    // The backend will handle final validation and may reject invalid values
    console.log('🎯 About to call onValueChange with:', fieldPath, valueToStore);
    onValueChange(fieldPath, valueToStore);
  };
  // Handle slider release - trigger the actual API call
  const handleSliderRelease = () => {
    if (pendingSliderValue !== null) {
      onValueChange(fieldPath, pendingSliderValue);
      setPendingSliderValue(null);
    }
  };

  // Handle slider start dragging
  const handleSliderStart = () => {
    // This function is kept for potential future use or if we want to add visual feedback
  };
  // Create appropriate input based on validation rules with enhanced UX
  const renderInput = () => {
    const fieldType = rules?.type || 'text';
    const isPercent = fieldType === 'percent';
    const isNumber = fieldType === 'number' || isPercent;
    
    // For boolean values - enhanced toggle
    if (fieldType === 'boolean' || typeof currentValue === 'boolean') {
      return (
        <div className="flex items-center space-x-3">
          <div className="relative inline-flex items-center">
            <input
              type="checkbox"
              checked={Boolean(watchedValue)}
              {...register(fieldPath)}
              onChange={(e) => handleValueChange(e.target.checked)}
              className="sr-only"
              id={`${fieldPath}-toggle`}
            />
            <label
              htmlFor={`${fieldPath}-toggle`}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 cursor-pointer ${
                Boolean(watchedValue) 
                  ? 'bg-indigo-600' 
                  : 'bg-white/20'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  Boolean(watchedValue) ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </label>
          </div>          <span className="text-sm text-white/70">
            {Boolean(watchedValue) ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      );
    }    // For number/percent values with enhanced UI
    if (isNumber || typeof currentValue === 'number') {
      // Phase 2: Simplified display value - handle zero values properly
      const displayValue = watchedValue !== undefined && watchedValue !== null && watchedValue !== '' 
        ? Number(watchedValue) 
        : watchedValue === 0 ? 0 : '';
      
      // Phase 2: Simplified min/max values - use rules directly
      const displayMinVal = rules?.min ?? (isPercent ? 0 : undefined);
      const displayMaxVal = rules?.max ?? (isPercent ? 100 : undefined);
      
      const step = isPercent ? 1 : 0.01; // Whole numbers for percentages
      
      return (
        <div className="space-y-3">          <div className="relative">
            <input
              type="number"
              step={step}
              min={displayMinVal}
              max={displayMaxVal}
              value={isTyping ? typingValue : displayValue}
              {...register(fieldPath)}              onChange={(e) => {
                const inputValue = e.target.value;
                
                // Set typing state and store the raw input value
                setIsTyping(true);
                setTypingValue(inputValue);
                
                // Clear any previous validation errors while typing
                setValidationError(null);
                
                // For typing state, skip all validation to avoid confusing the user
                // Validation will happen on blur when they finish typing
                
                // Only show validation errors for obviously invalid cases during typing
                if (inputValue !== '' && inputValue !== '-' && !inputValue.endsWith('.')) {
                  const numericValue = parseFloat(inputValue);
                  if (!isNaN(numericValue)) {
                    // For percent fields, only validate obviously out-of-range values during typing
                    if (isPercent) {
                      // Only show error if the display value is clearly out of reasonable range
                      if (numericValue < 0 || numericValue > 1000) {
                        setValidationError(`Value should be between 0 and 1000%`);
                      }
                    } else {
                      // For non-percent numbers, only validate extreme ranges during typing
                      if (rules?.min !== undefined && numericValue < rules.min * 0.1) {
                        setValidationError(`Value too small`);
                      } else if (rules?.max !== undefined && numericValue > rules.max * 10) {
                        setValidationError(`Value too large`);
                      }
                    }
                  }
                }
              }}              onBlur={(e) => {
                // Reset typing state
                setIsTyping(false);
                setTypingValue('');
                
                // Clear any typing validation errors
                setValidationError(null);
                
                // Validate and save the final value
                const inputValue = e.target.value;
                if (inputValue === '' || inputValue === '-') {
                  // Reset to current value if input is empty or invalid
                  return;
                }
                
                const val = parseFloat(inputValue);
                if (isNaN(val)) {
                  // Reset to current value if input is invalid
                  return;
                }
                
                // Now perform full validation and save
                handleValueChange(val);
              }}
              onKeyDown={(e) => {
                // Handle Enter key to save immediately
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}              className={`w-full px-3 py-2 pr-8 border ${
                validationError 
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                  : 'border-white/30 focus:ring-blue-500 focus:border-blue-500'
              } rounded-md shadow-sm focus:outline-none focus:ring-2 bg-white/10 backdrop-blur-sm text-white transition-colors`}
            />
            {isPercent && (
              <span className="absolute right-3 top-2 text-white/70 pointer-events-none">
                %
              </span>
            )}
          </div>{/* Enhanced range slider for better UX */}
          {(displayMinVal !== undefined && displayMaxVal !== undefined) && (            <div className="bg-white/5 backdrop-blur-sm p-3 rounded-lg border border-white/10">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-white/70">
                  {displayMinVal}{isPercent ? '%' : ''}
                </span>
                <span className="text-xs font-medium text-white/90">
                  Current: {isTyping ? 
                    `${typingValue}${isPercent ? '%' : ''}` :
                    (pendingSliderValue !== null ? 
                      pendingSliderValue : 
                      displayValue)}{isTyping ? '' : (isPercent ? '%' : '')}
                </span>
                <span className="text-xs font-medium text-white/70">
                  {displayMaxVal}{isPercent ? '%' : ''}
                </span>
              </div>              <input
                type="range"
                min={displayMinVal}
                max={displayMaxVal}
                step={step}
                value={isTyping ? (parseFloat(typingValue) || displayValue) : (pendingSliderValue !== null ? 
                  pendingSliderValue : 
                  displayValue)}
                onMouseDown={() => {
                  handleSliderStart();
                  // Don't interfere if user is typing
                  if (isTyping) {
                    setIsTyping(false);
                    setTypingValue('');
                  }
                }}
                onTouchStart={() => {
                  handleSliderStart();
                  if (isTyping) {
                    setIsTyping(false);
                    setTypingValue('');
                  }
                }}
                onChange={(e) => handleValueChange(parseFloat(e.target.value), true)}
                onMouseUp={handleSliderRelease}
                onTouchEnd={handleSliderRelease}
                onKeyUp={handleSliderRelease}
                className="w-full h-2 bg-white/20 backdrop-blur-sm rounded-lg appearance-none cursor-pointer border border-white/10"
                style={{
                  background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${(Number(isTyping ? (parseFloat(typingValue) || displayValue) : (pendingSliderValue !== null ? 
                    pendingSliderValue : 
                    displayValue)) - Number(displayMinVal)) / (Number(displayMaxVal) - Number(displayMinVal)) * 100}%, #e5e7eb ${(Number(isTyping ? (parseFloat(typingValue) || displayValue) : (pendingSliderValue !== null ? 
                    pendingSliderValue : 
                    displayValue)) - Number(displayMinVal)) / (Number(displayMaxVal) - Number(displayMinVal)) * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>
          )}
          
          {/* Show validation hints */}
          {(displayMinVal !== undefined || displayMaxVal !== undefined) && !validationError && (
            <div className="text-xs text-white/70 bg-blue-500/20 backdrop-blur-sm p-2 rounded border border-blue-400/30">
              <span className="font-medium">Valid range:</span> {displayMinVal !== undefined ? `${displayMinVal}${isPercent ? '%' : ''}` : '∞'} - {displayMaxVal !== undefined ? `${displayMaxVal}${isPercent ? '%' : ''}` : '∞'}
            </div>
          )}
        </div>
      );
    }

    // For string enum values with enhanced dropdown
    if (rules?.enum && rules.enum.length > 0) {
      return (
        <div className="relative">          <select
            value={String(watchedValue)}
            {...register(fieldPath)}
            onChange={(e) => handleValueChange(e.target.value)}            className={`w-full px-3 py-2 border ${
              validationError 
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                : 'border-white/30 focus:ring-blue-500 focus:border-blue-500'
            } rounded-md shadow-sm focus:outline-none focus:ring-2 bg-slate-800/95 backdrop-blur-sm text-white transition-colors cursor-pointer`}
            style={{
              colorScheme: 'dark'
            }}
          >
            {rules.enum.map((option) => (
              <option key={option} value={option} style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                {String(option).charAt(0).toUpperCase() + String(option).slice(1)}
              </option>
            ))}
          </select>
          {/* Show available options */}          <div className="mt-1 text-xs text-white/70">
            Options: {rules.enum.map(opt => String(opt)).join(', ')}
          </div>
        </div>
      );
    }

    // For arrays - enhanced JSON display with editing support
    if (Array.isArray(currentValue)) {
      return (
        <div className="space-y-3">
          <div className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/20">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-white/80">Array Configuration</span>
              <span className="text-xs text-white/60">Length: {currentValue.length}</span>
            </div>
            <pre className="text-xs text-white/60 whitespace-pre-wrap bg-white/10 backdrop-blur-sm p-3 rounded border border-white/20 overflow-x-auto">
              {JSON.stringify(currentValue, null, 2)}
            </pre>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 p-3 rounded-lg">
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> Array configurations require advanced editing. Contact support or use the API to modify.
            </p>
          </div>
        </div>
      );
    }

    // Default text input with pattern validation support
    const inputType = fieldType === 'string' ? 'text' : 'text';
    const hasPattern = rules?.pattern;
    
    return (
      <div className="space-y-2">
        <input
          type={inputType}
          value={String(watchedValue || '')}
          {...register(fieldPath)}
          onChange={(e) => handleValueChange(e.target.value)}
          pattern={hasPattern ? rules.pattern : undefined}          className={`w-full px-3 py-2 border ${
            validationError 
              ? 'border-red-400 focus:ring-red-400 focus:border-red-400' 
              : 'border-white/20 focus:ring-blue-400 focus:border-transparent'
          } rounded-md shadow-sm focus:outline-none focus:ring-2 bg-white/10 backdrop-blur-sm text-white placeholder:text-white/70 transition-colors`}
        />
        
        {/* Show pattern hint */}
        {hasPattern && !validationError && (
          <div className="text-xs text-white/70 bg-blue-400/20 backdrop-blur-sm p-2 rounded">
            <span className="font-medium">Pattern:</span> <code className="text-xs bg-white/20 backdrop-blur-sm px-1 rounded">{rules.pattern}</code>
          </div>
        )}
        
        {/* Show length constraints */}
        {(rules?.min_length !== undefined || rules?.max_length !== undefined) && !validationError && (
          <div className="text-xs text-white/70">
            Length: {rules.min_length !== undefined ? `min ${rules.min_length}` : ''} {rules.max_length !== undefined ? `max ${rules.max_length}` : ''}
          </div>
        )}
      </div>
    );
  };

  // For primitive values, render appropriate input
  return (    <div className={`border border-white/20 bg-white/5 backdrop-blur-sm rounded-lg p-4 ${indentClass}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <label className={`block text-sm font-medium text-white/90 capitalize mb-1 ${depth > 0 ? 'text-xs' : ''}`}>
            {fieldKey.replace(/_/g, ' ')}
          </label>
          
          {/* Field description */}
          {metadata?.description && (
            <p className="text-xs text-white/70 mb-2">{metadata.description}</p>
          )}
            <div className="flex items-center space-x-4 text-xs text-white/60">
            <span>
              Current: <strong className={hasUserOverride ? 'text-white/90' : 'text-white/90'}>
                {isPercent ? `${currentValue}%` : String(currentValue)}
              </strong>
              {hasUserOverride && <span className="ml-1 text-white/70">(Custom)</span>}
            </span>
            {defaultValue !== undefined && (
              <span>Default: <strong>
                {isPercent ? `${defaultValue}%` : String(defaultValue)}
              </strong></span>
            )}
            {isModified && (
              <span className="text-orange-600 dark:text-orange-400 font-medium">Modified</span>
            )}
            {!hasUserOverride && (
              <span className="text-white/60">Using Default</span>
            )}
          </div>
        </div>
        {hasUserOverride && (
          <button
            type="button"
            onClick={() => onRemoveOverride(fieldPath)}
            className="ml-2 px-2 py-1 text-xs border border-white/20 bg-white/10 backdrop-blur-sm rounded text-white/80 hover:bg-white/20 hover:text-red-400 transition-all duration-200"
            title="Revert to default"
          >
            Reset
          </button>
        )}
      </div>
      
      {/* Render the appropriate input */}
      {renderInput()}
      
      {/* Show validation error */}
      {validationError && (
        <div className="mt-2 text-sm text-red-600 dark:text-red-400">
          {validationError}
        </div>
      )}
    </div>
  );
};


// --- Type for react-select options ---
interface TimezoneOption {
  value: string;
  label: string;
}

// --- Type for Exchange options ---
interface ExchangeOption {
  value: string;
  label: string;
}


// --- Main Settings Component ---

const Settings = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { isQueryingAllowed } = useGlobalQueryStore();
  
  // Auth Store - get all needed functions for sync functionality
  const { isApiAuthReady, setSyncTaskId, setSyncTaskStatus } = useAuthStore(state => ({
    isApiAuthReady: state.isApiAuthReady,
    setSyncTaskId: state.setSyncTaskId,
    setSyncTaskStatus: state.setSyncTaskStatus,
  }));
  
  const queryClient = useQueryClient();
  
  // SECURITY: Get user context for secure cache isolation
  const { userId, isReady: isUserContextReady } = useUserContext();

  // --- Local UI State ---
  const [isAccountsExpanded, setAccountsExpanded] = useState(false); // Start collapsed
  const [isAddingAccount, setAddingAccount] = useState(false); // State for inline add form
  // Remove local syncTaskId state
  // const [syncTaskId, setSyncTaskId] = useState<string | null>(null); // State for sync task ID
  // --- Modal State ---
  const [isEditAccountModalOpen, setEditAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<UserAccountResponse | null>(null);
  const [showApiKeyId, setShowApiKeyId] = useState<number | null>(null);
  
  // Delete Account Modal State
  const [isDeleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState<UserAccountResponse | null>(null);
  // --- React Query Hooks ---
  // SECURITY: User Settings with user-aware cache key
  const { data: userSettings, isLoading: isLoadingUserSettings } = useQuery<UserSettingsResponse, Error>({
    queryKey: userId ? UserAwareCacheKeys.userSettings(userId) : ['disabled'],
    queryFn: fetchUserSettings,
    enabled: !!isApiAuthReady && isQueryingAllowed && isUserContextReady && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    placeholderData: keepPreviousData, // Keep previous data while fetching
  });

  const updateSettingsMutation = useMutation<UserSettingsResponse, Error, UserSettingsUpdate>( {
    mutationFn: updateUserSettings, // Use mutationFn property
    onSuccess: (data) => {
      if (userId) {
        queryClient.setQueryData(UserAwareCacheKeys.userSettings(userId), data); // SECURITY: User-aware cache key
      }
      // toast.success('Profile & Billing updated!'); // Handled by toast.promise
    },
    onError: () => { // Removed unused 'error' parameter
      // toast.error(`Failed to update settings: ${error.message}`); // Handled by toast.promise
    }
  });  // SECURITY: Subscription with user-aware cache key
  const { data: subscription, isLoading: isLoadingSubscription, error: subscriptionError } = useQuery<SubscriptionResponse, Error>({
    queryKey: userId ? UserAwareCacheKeys.subscription(userId) : ['disabled'],
    queryFn: fetchSubscription,
    enabled: !!isApiAuthReady && isQueryingAllowed && isUserContextReady && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    placeholderData: keepPreviousData, // Keep previous data while fetching
  });
  // SECURITY: Linked Accounts with user-aware cache key
  const { data: linkedAccounts, isLoading: isLoadingAccounts } = useQuery<UserAccountResponse[], Error>({
    queryKey: userId ? UserAwareCacheKeys.linkedAccounts(userId) : ['disabled'],
    queryFn: fetchLinkedAccounts,
    enabled: !!isApiAuthReady && isQueryingAllowed && isUserContextReady && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    placeholderData: keepPreviousData, // Keep previous data while fetching
  });
  const createAccountMutation = useMutation<UserAccountResponse, Error, UserAccountCreate>( {
    mutationFn: createLinkedAccount, // Use mutationFn property
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.linkedAccounts(userId) }); // SECURITY: User-aware cache key
      }
      setAddingAccount(false); // Close inline form on success
      resetAccountForm(); // Reset form fields
      
      // Automatically trigger trade sync for the newly added account
      toast.loading('Initiating trade sync for new account...', { id: 'auto-sync-new-account' });
      tradeSyncMutation.mutate();
      
      // toast.success('Account linked successfully!'); // Handled by toast.promise
    },
    onError: () => { // Removed unused 'error' parameter
      // toast.error(`Failed to link account: ${error.message}`); // Handled by toast.promise
    }
  });

  const updateAccountMutation = useMutation<UserAccountResponse, Error, { accountId: number; data: UserAccountUpdate }>( {
    mutationFn: ({ accountId, data }) => updateLinkedAccount(accountId, data), // Use mutationFn property
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.linkedAccounts(userId) }); // SECURITY: User-aware cache key
      }
      setEditAccountModalOpen(false);
      setEditingAccount(null);
      // toast.success('Account updated successfully!'); // Handled by toast.promise
    },
    onError: () => { // Removed unused 'error' parameter
      // toast.error(`Failed to update account: ${error.message}`); // Handled by toast.promise
    }
  });

  const deleteAccountMutation = useMutation<void, Error, number>( {
    mutationFn: deleteLinkedAccount, // Use mutationFn property
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.linkedAccounts(userId) }); // SECURITY: User-aware cache key
      }
      // toast.success('Account deleted successfully!'); // Handled by toast.promise
    },
    onError: () => { // Removed unused 'error' parameter
      // toast.error(`Failed to delete account: ${error.message}`); // Handled by toast.promise
    }
  });
  // Mutation to create Stripe portal session
  const portalMutation = useMutation< { url: string }, Error, void >({
    mutationFn: createCustomerPortalSession,
    onSuccess: (data) => {
      if (data.url) {
        toast.dismiss(); // Dismiss loading toast
        window.open(data.url, '_blank', 'noopener,noreferrer'); // Open in new tab
      } else {
        toast.error('Could not retrieve portal URL.');
      }
    },
    onError: (error) => {
      toast.error(`Error accessing billing portal: ${error.message}`);
    },  });

  // Timezone Confirmation Mutation
  const confirmTimezoneMutation = useMutation<{ success: boolean; timezone: string; timezone_confirmed: boolean }, Error, string>({
    mutationFn: confirmTimezone,
    onSuccess: (_data) => {
      // Invalidate user settings to refresh the data
      if (userId) {
        queryClient.invalidateQueries({ queryKey: UserAwareCacheKeys.userSettings(userId) });
      }
      toast.success('Timezone confirmed successfully!');
    },
    onError: (error) => {
      console.error('Error confirming timezone:', error);
      toast.error('Failed to confirm timezone. Please try again.');
    },
  });

  // Trade Sync Mutation - reusable sync functionality
  interface SyncResponse {
    status: string;
    task_id: string | null;
  }
    const tradeSyncMutation = useMutation<SyncResponse, Error>({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await apiClient.post('/trades/sync', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Dismiss any loading toasts
      toast.dismiss('auto-sync-new-account');
      
      if (data.task_id) {
        toast.success('Trade sync started in background.');
        setSyncTaskId(data.task_id, 'trades');
        setSyncTaskStatus({
          status: 'pending',
          message: 'Sync initiated, connecting...',
          progress: 0,
          details: [`${new Date().toISOString()}: Sync initiated.`]
        });
      } else if (data.status === 'no accounts configured') {
        toast('No accounts configured for sync.');
        setSyncTaskId(null);
        setSyncTaskStatus(null);
      } else {
        toast.success('Sync request accepted, but no task ID returned.');
        setSyncTaskId(null);
        setSyncTaskStatus(null);
      }
    },
    onError: (error) => {
      // Dismiss any loading toasts
      toast.dismiss('auto-sync-new-account');
      
      console.error('Sync initiation failed:', error);
      toast.error(`Sync failed: ${error.message || 'Unknown error'}`);
    },
  });

  // --- Portfolio Configuration State ---
  const [isPortfolioExpanded, setPortfolioExpanded] = useState(false);  // Portfolio Config Categories Query
  const { data: portfolioCategories, isLoading: isLoadingCategories } = useQuery<PortfolioConfigCategories, Error>({
    queryKey: userId ? UserAwareCacheKeys.portfolioCategories(userId) : ['disabled'],
    queryFn: async () => {
      const result = await getPortfolioConfigCategories();
      return result;
    },
    enabled: !!isApiAuthReady && isQueryingAllowed && isUserContextReady && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    placeholderData: keepPreviousData, // Keep previous data while fetching
  });

  // Log what portfolioCategories contains after it's loaded
  useEffect(() => {
    if (portfolioCategories) {
      console.log('📦 portfolioCategories state contains:', portfolioCategories);
    }
  }, [portfolioCategories]);
  
  // Note: Category-specific config data is now fetched by individual CategoryCard components
  // This eliminates duplicate API calls

  // Portfolio Config Update Mutation - Updated
  const updatePortfolioConfigMutation = useMutation<
    { message: string; category: string; key: string; value: any }, 
    Error, 
    { category: string; key: string; request: UpdatePortfolioConfigRequest }
  >({
    mutationFn: ({ category, key, request }) => updatePortfolioConfig(category, key, request),
    onMutate: async ({ category, key, request }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['portfolio-category-config', category] });

      // SECURITY: Snapshot the previous value with user-aware cache key
      const categoryConfigKey = userId ? UserAwareCacheKeys.portfolioCategoryConfig(userId, category) : ['disabled'];
      const previousConfig = queryClient.getQueryData(categoryConfigKey);

      // Optimistically update to the new value
      if (previousConfig && userId) {
        queryClient.setQueryData(categoryConfigKey, (old: any) => {
          if (!old) return old;
          
          const newConfigs = { ...old.configs };
          if (newConfigs[key]) {
            newConfigs[key] = {
              ...newConfigs[key],
              value: request.value,
              has_user_override: true // Mark as having override since we're updating
            };
          }
          
          // Calculate if category has any overrides
          const hasAnyOverrides = Object.values(newConfigs).some((config: any) => config.has_user_override);
          
          return {
            ...old,
            configs: newConfigs,
            has_any_overrides: hasAnyOverrides // Update category-level override flag
          };
        });
      }

      // Return a context object with the snapshotted value
      return { previousConfig };
    },
    onSuccess: (_data, variables) => {
      // SECURITY: Invalidate and refetch to ensure we have the latest server state
      if (userId) {
        queryClient.invalidateQueries({ 
          queryKey: UserAwareCacheKeys.portfolioCategoryConfig(userId, variables.category),
          exact: true 
        });
      }
      
      toast.success('Configuration updated successfully!');
    },
    onError: (error: any, variables, context: unknown) => {
      // SECURITY: If the mutation fails, use the context returned from onMutate to roll back
      const mutationContext = context as { previousConfig?: any } | undefined;
      if (mutationContext?.previousConfig && userId) {
        const categoryConfigKey = UserAwareCacheKeys.portfolioCategoryConfig(userId, variables.category);
        queryClient.setQueryData(categoryConfigKey, mutationContext.previousConfig);
      }
      
      // Extract detailed error message from API response
      let errorMessage = 'Failed to update configuration';
      
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = `Failed to update configuration: ${error.message}`;
      }
      
      toast.error(errorMessage);
    },
    onSettled: (_data, _error, variables) => {
      // SECURITY: Always refetch after error or success to ensure server state
      if (userId) {
        queryClient.invalidateQueries({ 
          queryKey: UserAwareCacheKeys.portfolioCategoryConfig(userId, variables.category),
          exact: true 
        });
      }
    },
  });
  // Portfolio Config Reset Mutation - Updated
  const resetPortfolioConfigMutation = useMutation<{ message: string; category: string }, Error, string>({
    mutationFn: resetPortfolioCategoryToDefaults,
    onSuccess: (_, category) => {
      // Only invalidate the specific category query used by CategoryCard
      queryClient.invalidateQueries({ 
        queryKey: ['portfolio-category-config', category],
        exact: true 
      });
      
      toast.success('Category reset to defaults successfully!');
    },
    onError: (error: any) => {
      // Extract detailed error message from API response
      let errorMessage = 'Failed to reset configuration';
      
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = `Failed to reset configuration: ${error.message}`;
      }
      
      toast.error(errorMessage);
    },
  });
  
  // Portfolio Config Delete Override Mutation - New
  const deletePortfolioConfigMutation = useMutation<
    { message: string; category: string; key: string }, 
    Error, 
    { category: string; key: string }
  >({
    mutationFn: ({ category, key }) => deletePortfolioConfigOverride(category, key),
    onSuccess: (_, variables) => {
      // Only invalidate the specific category query used by CategoryCard
      queryClient.invalidateQueries({ 
        queryKey: ['portfolio-category-config', variables.category],
        exact: true 
      });
      
      toast.success('Configuration override removed, reverted to default!');
    },
    onError: (error: any) => {
      // Extract detailed error message from API response
      let errorMessage = 'Failed to remove configuration override';
      
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = `Failed to remove configuration override: ${error.message}`;
      }
      
      toast.error(errorMessage);
    },
  });

  // --- Form Instances ---
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors, isSubmitting: isSubmittingProfile },
    reset: resetProfileForm,
    watch: watchProfile,
    control: profileControl, // Add control for Controller
  } = useForm<ProfileBillingInputs>();
  
  const {
    register: registerAccount,
    handleSubmit: handleAccountSubmit,
    formState: { errors: accountErrors, isSubmitting: isSubmittingAccount },
    reset: resetAccountForm,
    control: accountControl, // Add control for Controller
    watch: watchAccountForm, // Add watch to track exchange selection for conditional passphrase field
  } = useForm<LinkedAccountInputs>();
  
  // Portfolio Configuration Form - simplified for field registration and watching
  const {
    watch: watchPortfolioConfig,
    setValue: setPortfolioConfigValue,
  } = useForm<PortfolioConfigInputs>();

  // Watch form values removed - no longer needed

  // --- Effects ---

  // Reset Profile/Billing form when userSettings data loads
  useEffect(() => {
    if (userSettings) {
      // Attempt to detect timezone if none is set
      let defaultTimezone = userSettings.timezone || '';
      if (!defaultTimezone) {
          try {
              defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              // Validate if detected timezone is in our list or common list (optional, but good practice)
              // For simplicity, we'll allow any detected valid timezone for now.
          } catch (e) {
              console.warn("Could not detect browser timezone.", e);
              defaultTimezone = 'UTC'; // Fallback
          }
      }

      resetProfileForm({
        billing_email: userSettings.billing_email || '',
        billing_name: userSettings.billing_name || '',
        timezone: defaultTimezone, // Use detected or saved timezone
      });    }
  }, [userSettings, resetProfileForm]);

  // Note: Portfolio config form synchronization is now handled by individual CategoryCard components

  // --- Event Handlers ---

  // Profile/Billing Submit
  const onProfileSubmit: SubmitHandler<ProfileBillingInputs> = (data) => {
    toast.promise(
        updateSettingsMutation.mutateAsync(data),
        {
          loading: 'Saving profile...',
          success: 'Profile updated!',
          error: (err) => `Failed to update: ${err.message}`,
        }
    );
  };

  // Toggle Inline Add Account Form
  const toggleAddAccountForm = () => {
    setAddingAccount(prev => {
        if (!prev) { // If opening the form
            resetAccountForm({ account_name: '', api_key: '', api_secret: '', exchange_name: 'bybit' }); // Clear form, default exchange
            setEditingAccount(null); // Ensure we are not in edit mode
            setAccountsExpanded(false); // Collapse the list when opening add form
        }
        return !prev;
    });
  };

  // Toggle Accounts List Visibility
  const toggleAccountsList = () => {
      setAccountsExpanded(prev => {
          if (!prev) { // If opening the list
              setAddingAccount(false); // Close add form when opening list
          }
          return !prev;
      });
  };

  // Linked Account Modal Open/Close (Edit Only)
  const openEditAccountModal = (account: UserAccountResponse) => {
    setEditingAccount(account);
    resetAccountForm({
        id: account.id,
        account_name: account.account_name,
        api_key: account.api_key,
        api_secret: '', // Don't prefill secret for editing
        exchange_name: account.exchange_name, // Include exchange_name
    });
    setEditAccountModalOpen(true);
  };

  // Linked Account Submit (Create/Update)
  const onAccountSubmit: SubmitHandler<LinkedAccountInputs> = (data) => {
    if (editingAccount) { // Update (uses modal)
      const updateData: UserAccountUpdate = { account_name: data.account_name, api_key: data.api_key };
      if (data.api_secret) { // Only include secret if user entered a new one
        updateData.api_secret = data.api_secret;
      }
      if (data.passphrase) { // Include passphrase if provided (for Bitget)
        updateData.passphrase = data.passphrase;
      }
      toast.promise(
          updateAccountMutation.mutateAsync({ accountId: editingAccount.id, data: updateData }),
          {
            loading: 'Updating account...',
            success: 'Account updated!',
            // --- Updated Error Handler ---
            error: (err: any) => { // Use 'any' or a more specific error type if known (e.g., AxiosError)
                // Check for Axios-like error structure with response data detail
                if (err.response && err.response.data && err.response.data.detail) {
                    return `Update failed: ${err.response.data.detail}`;
                }
                // Fallback to generic message
                return `Update failed: ${err.message || 'An unknown error occurred'}`;
            },
            // --- End Updated Error Handler ---
          }
      );
    } else { // Create (uses inline form)
      const createData: UserAccountCreate = {
        account_name: data.account_name,
        api_key: data.api_key,
        api_secret: data.api_secret,
        exchange_name: data.exchange_name || 'bybit', // Ensure exchange_name is included, default if somehow null
        passphrase: data.passphrase || undefined, // Include passphrase if provided (for Bitget)
      };
      toast.promise(
          createAccountMutation.mutateAsync(createData),
          {
            loading: 'Linking account...',
            success: 'Account linked!',
            // --- Updated Error Handler ---
            error: (err: any) => { // Use 'any' or a more specific error type if known
                // Check for Axios-like error structure with response data detail
                if (err.response && err.response.data && err.response.data.detail) {
                    return `Linking failed: ${err.response.data.detail}`;
                }
                // Fallback to generic message
                return `Linking failed: ${err.message || 'An unknown error occurred'}`;
            },
            // --- End Updated Error Handler ---
          }
      );
    }
  };
  // Linked Account Delete - Open Modal
  const handleDeleteAccount = (account: UserAccountResponse) => {
    setDeletingAccount(account);
    setDeleteAccountModalOpen(true);
  };

  // Confirm Delete Account
  const confirmDeleteAccount = () => {
    if (deletingAccount) {
      toast.promise(
        deleteAccountMutation.mutateAsync(deletingAccount.id),
        {
          loading: 'Deleting account...',
          success: 'Account deleted!',
          error: (err) => `Deletion failed: ${err.message}`,
        }
      );
      setDeleteAccountModalOpen(false);
      setDeletingAccount(null);
    }
  };

  // Toggle API Key Visibility
  const toggleApiKeyVisibility = (accountId: number) => {
    setShowApiKeyId(prevId => (prevId === accountId ? null : accountId));
  };

  // Copy API Key
  const copyApiKey = (apiKey: string) => {
    navigator.clipboard.writeText(apiKey)
      .then(() => toast.success('API Key copied!'))
      .catch(() => toast.error('Failed to copy API Key'));
  };
  const handleManageSubscription = () => {
    toast.loading('Redirecting to billing portal...');
    portalMutation.mutate();
  };

  // Handle timezone confirmation
  const handleConfirmTimezone = (timezone?: string) => {
    if (!timezone) {
      toast.error('Please select a timezone first');
      return;
    }

    toast.loading('Confirming timezone...');
    confirmTimezoneMutation.mutate(timezone);
  };

  // --- Portfolio Configuration Event Handlers ---

  // Toggle Portfolio Settings Visibility
  const togglePortfolioSettings = () => {
    setPortfolioExpanded(prev => !prev);
  };

  // Handle individual config value change - Updated for proper path parsing
  const handleConfigValueChange = (path: string, value: number | boolean | string) => {
    console.log('🔧 handleConfigValueChange called with path:', path, 'value:', value);
    
    // Parse the path: format is "{category}.{fieldKey}" or "{category}.{fieldKey}.{nestedProperty}"
    const pathParts = path.split('.');
    
    if (pathParts.length < 2) {
      console.error('Invalid path format:', path);
      return;
    }
    
    const category = pathParts[0]; // e.g., "stability" (the actual category from API)
    const fieldKey = pathParts[1];  // e.g., "deviation_thresholds" (the config field)
    
    // Note: Removed activePortfolioCategory validation since categories are handled individually
    console.log('🎯 Parsed path - category:', category, 'fieldKey:', fieldKey);
    
    // Get current form state for this field
    const currentFieldValue = watchPortfolioConfig(fieldKey, {});
    
    // Helper function to convert value for storage if it's a percent field
    const convertValueForStorage = (val: any, fieldPath: string): any => {
      if (typeof val === 'number') {
        // Check if this looks like a percent field based on path/name
        const isPercentField = fieldPath.toLowerCase().includes('percent') || 
                              fieldPath.toLowerCase().includes('rate') ||
                              fieldPath.toLowerCase().includes('ratio');
        
        if (isPercentField) {
          // For percent fields, assume fractional format (0.05 for 5%) for storage
          // The display format is handled in the NestedConfigField component
          return val / 100; // Convert from display percentage to fractional
        }
      }
      return val;
    };
    
    // Create updated value based on whether this is a nested property or top-level field
    let updatedValue;
    if (pathParts.length > 2) {
      // Handle nested property: "{category}.{fieldKey}.{nestedProperty}"
      const nestedProperty = pathParts[2];
      updatedValue = { ...currentFieldValue };
      updatedValue[nestedProperty] = convertValueForStorage(value, nestedProperty);
    } else {
      // Handle top-level field: "{category}.{fieldKey}"
      updatedValue = convertValueForStorage(value, fieldKey);
    }
    
    // Update form state (keep display values in form)
    setPortfolioConfigValue(fieldKey, updatedValue);
    
    // Auto-save on change with corrected request structure
    console.log('🚀 Calling updatePortfolioConfigMutation.mutate with:', {
      category: category,
      key: fieldKey,
      request: { value: updatedValue, config_name: 'default' },
    });
    
    updatePortfolioConfigMutation.mutate({
      category: category,
      key: fieldKey,
      request: { value: updatedValue, config_name: 'default' },
    });
  };

  // Handle removing user override for a specific config - Updated for nested paths
  // Handle removing user override for a specific config - Updated for proper path parsing
  const handleRemoveConfigOverride = (path: string) => {
    // Parse the path: format is "{category}.{fieldKey}" or "{category}.{fieldKey}.{nestedProperty}"
    const pathParts = path.split('.');
    
    if (pathParts.length < 2) {
      console.error('Invalid path format:', path);
      return;
    }
    
    const category = pathParts[0]; // e.g., "lifecycle"
    const fieldKey = pathParts[1];  // e.g., "deviation_thresholds" (the config field)
    const displayPath = path.replace(/\./g, ' → ').replace(/_/g, ' ');
    
    // Note: Removed activePortfolioCategory validation since categories are handled individually
    console.log('🗑️ Remove override - category:', category, 'fieldKey:', fieldKey);
    
    if (window.confirm(`Are you sure you want to remove your custom setting for ${displayPath} and revert to the default value?`)) {
      deletePortfolioConfigMutation.mutate({
        category: category,
        key: fieldKey,
      });
    }
  };
  // Reset category to defaults
  const handleResetCategory = (categoryToReset: string) => {
    if (window.confirm(`Are you sure you want to reset all ${categoryToReset} settings to defaults? This cannot be undone.`)) {
      resetPortfolioConfigMutation.mutate(categoryToReset);
    }
  };



  // --- Derived State ---

  // Generate timezone lists for react-select dropdown with optgroups
  const timezoneOptions: OptionsOrGroups<TimezoneOption, GroupBase<TimezoneOption>> = useMemo(() => {
    let detectedTimezone: string | null = null;
    try {
      detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      console.warn("Could not detect browser timezone.", e);
    }

    const suggestedValues = [...commonTimezones];

    // Add detected timezone if it's valid and not already in the common list
    if (detectedTimezone && !suggestedValues.includes(detectedTimezone)) {
      if (detectedTimezone.includes('/')) {
          suggestedValues.unshift(detectedTimezone); // Add to the beginning
      }
    }
    if (!suggestedValues.includes('UTC')) {
        suggestedValues.unshift('UTC');
    }

    // Get all timezones supported by the browser
    let allTimezones: string[] = [];
    try {
        allTimezones = (Intl as any).supportedValuesOf?.('timeZone') || [];
    } catch (e) {
        console.error("Failed to get all supported timezones. Falling back.", e);
        allTimezones = [...suggestedValues]; // Fallback
    }

    // Create options for react-select
    const formatLabel = (tz: string) => tz.replace(/_/g, ' ');
    const suggestedOptions: TimezoneOption[] = suggestedValues.map(tz => ({ value: tz, label: formatLabel(tz) }));
    const otherOptions: TimezoneOption[] = allTimezones
        .filter(tz => !suggestedValues.includes(tz))
        .sort()
        .map(tz => ({ value: tz, label: formatLabel(tz) }));

    // Initialize as a mutable array of groups
    const groupedOptions: GroupBase<TimezoneOption>[] = [];
    if (suggestedOptions.length > 0) {
        groupedOptions.push({ label: 'Suggested Timezones', options: suggestedOptions });
    }
    if (otherOptions.length > 0) {
        groupedOptions.push({ label: 'Other Options', options: otherOptions });
    }

    return groupedOptions;
  }, []); // Empty dependency array means this runs once

  // --- Exchange Options for react-select ---
  const exchangeOptions: ExchangeOption[] = [
    { value: 'bybit', label: 'Bybit' },
    { value: 'binance', label: 'Binance' },
    { value: 'bitget', label: 'Bitget' },
  ];


  // --- Render Logic ---  // Framer Motion variants
  // Use variants similar to BotMaintenancePage
  const collapseVariants = {
    open: { opacity: 1, height: 'auto', marginTop: '0rem', marginBottom: '0rem', overflow: 'hidden' }, // Adjusted margins
    collapsed: { opacity: 0, height: 0, marginTop: '0rem', marginBottom: '0rem', overflow: 'hidden' },
  };
  const listCollapseVariants = { // Same as collapseVariants now
    open: { opacity: 1, height: 'auto', marginTop: '0rem', marginBottom: '0rem', overflow: 'hidden' },
    collapsed: { opacity: 0, height: 0, marginTop: '0rem', marginBottom: '0rem', overflow: 'hidden' },
  };

  // --- react-select Glassmorphism styles to match FormInput exactly ---
  const selectClassNames = {
    control: (state: { isFocused: boolean }) =>
      `w-full px-3 py-2 border ${
        profileErrors.timezone ? 'border-red-400' : 'border-white/20'
      } rounded-md shadow-sm bg-white/10 backdrop-blur-sm text-white ${
        state.isFocused ? 'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent' : ''
      } hover:border-white/30 min-h-[2.5rem] transition-colors`,
    valueContainer: () => 'py-0 px-0',
    input: () => 'text-white placeholder:text-white/70',
    placeholder: () => 'text-white/70',
    singleValue: () => 'text-white',
    menu: () => 'mt-1 absolute z-[9999] w-full bg-white/95 backdrop-blur-md rounded-md shadow-xl border border-white/30',
    menuList: () => 'max-h-60 overflow-y-auto p-1',
    option: (state: { isFocused: boolean; isSelected: boolean }) =>
      `px-3 py-2 text-sm rounded-md cursor-pointer ${
        state.isSelected
          ? 'bg-blue-600/80 text-white'
          : state.isFocused
          ? 'bg-white/30 text-gray-800'
          : 'text-gray-700'
      }`,
    groupHeading: () => 'px-3 py-2 text-xs font-semibold text-gray-600 uppercase',
    indicatorSeparator: () => 'bg-white/30',
    dropdownIndicator: () => 'text-white/60 hover:text-white/80 p-2',
    clearIndicator: () => 'text-white/60 hover:text-red-400 p-2',
    noOptionsMessage: () => 'px-3 py-2 text-sm text-gray-600',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Animated background blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        className="p-4 md:p-8 min-h-screen text-gray-100 relative z-10"
      >        <motion.h1 
          className="text-4xl font-bold mb-8 text-center bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text leading-tight pb-1"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Settings
        </motion.h1>

      {/* ... queryError display ... */}      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Left Column (Forms) */}
        <div className="lg:col-span-2 space-y-8">          {/* Profile Section */}
          <motion.section 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeInOut" } }
            }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 p-6 rounded-xl shadow-xl"
          >            <h2 className="text-xl font-semibold mb-4 flex items-center text-white">
              <UserIcon className="w-6 h-6 mr-2 text-blue-400" />
              Profile
            </h2>
            {isLoadingUserSettings ? (
              <div className="space-y-4">
                <SkeletonLoader />
                <SkeletonLoader />
                <SkeletonLoader className="h-4 w-1/2" />
                <SkeletonLoader className="h-4 w-1/2" />
                <SkeletonLoader className="h-4 w-1/2" /> {/* Skeleton for timezone */}
              </div>
            ) : (
              <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
                <FormInput label="Billing Email" id="billing_email" type="email" register={registerProfile} errors={profileErrors} />
                <FormInput label="Billing Name" id="billing_name" register={registerProfile} errors={profileErrors} />                {/* Timezone Select - Use react-select with Controller */}
                <div>
                  <label htmlFor="timezone-select" className="block text-sm font-medium text-white/90 mb-1">Timezone</label>
                  <Controller
                    name="timezone"
                    control={profileControl}
                    rules={{ required: 'Timezone is required' }}
                    render={({ field }) => (                      <Select<TimezoneOption, false, GroupBase<TimezoneOption>> // Explicit types
                        inputId="timezone-select"
                        {...field}
                        options={timezoneOptions}
                        // Safely access options only from groups within flatMap
                        value={
                          timezoneOptions
                            .flatMap(group => ('options' in group ? group.options : [])) // Check if it's a group before accessing options
                            .find(option => option.value === field.value) || null
                        }
                        onChange={selectedOption => field.onChange(selectedOption ? selectedOption.value : '')} // Update form state with the value
                        isClearable
                        isSearchable
                        placeholder="Select or type timezone..."
                        classNames={selectClassNames} // Apply Tailwind styles
                        unstyled={true} // Remove all default react-select styles
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} // Render dropdown in document body to bypass container constraints
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                        // Optional: for better accessibility and form integration
                        onBlur={field.onBlur}
                        instanceId="timezone-select-instance" // Unique ID for SSR or multiple selects
                      />
                    )}
                  />
                  {profileErrors.timezone && <p className="mt-1 text-xs text-red-400">{profileErrors.timezone.message}</p>}
                  
                  {/* New: Timezone confirmation status */}
                  {userSettings && !userSettings.timezone_confirmed && (
                    <div className="mt-2 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-yellow-200 font-medium text-sm">Timezone Not Confirmed</h4>
                          <p className="text-yellow-200/80 text-xs mt-1">
                            Please confirm your timezone is correct for accurate time displays throughout the dashboard.
                          </p>
                          <button
                            onClick={() => handleConfirmTimezone(watchProfile('timezone') || undefined)}
                            disabled={confirmTimezoneMutation.isPending}
                            className={`mt-2 text-xs px-3 py-1 rounded border border-yellow-500/50 transition-colors ${
                              confirmTimezoneMutation.isPending
                                ? 'bg-yellow-600/30 text-yellow-200/60 cursor-not-allowed'
                                : 'bg-yellow-600/50 hover:bg-yellow-600/70 text-yellow-100'
                            }`}
                          >
                            {confirmTimezoneMutation.isPending ? 'Confirming...' : 'Confirm Current Timezone'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-4 border-t border-white/20">
                  <button
                    type="submit"
                    disabled={isSubmittingProfile || updateSettingsMutation.isPending}
                    className="px-4 py-2 text-sm rounded-md font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    {isSubmittingProfile || updateSettingsMutation.isPending ? 'Saving...' : 'Save Profile'}
                  </button>
                </div>
              </form>
            )}

            {/* Privacy & Communication embedded section */}
            <div className="mt-6 pt-6 border-t border-white/20">
              <CookiePreferencesSection />
            </div>
          </motion.section>

          {/* Linked Accounts Section */}
          <motion.section 
            layout 
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeInOut" } }
            }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 shadow-xl rounded-xl overflow-hidden"
          >             {/* Section Header */}
             <div className="p-4 md:p-6 border-b border-white/20 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white/90 flex items-center">
                    <ServerIcon className="w-5 h-5 mr-2 text-white/60"/>
                    Linked Accounts
                </h2>
                {/* Update TradeSyncButton call - remove onSyncStart */}
                <TradeSyncButton />
             </div>

             {/* Render Sync Status Indicator below the button */}
             <SyncStatusIndicator />


             {/* Add New Account (Collapsible Button & Form) */}
             <div className="border-b border-white/20">
                <button
                    onClick={toggleAddAccountForm}                    className={`w-full flex justify-between items-center p-4 text-left text-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400/50 cursor-pointer ${ // Added cursor-pointer
                        isAddingAccount
                        ? 'text-white font-semibold bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg' // Style when open
                        : 'text-white font-semibold bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 shadow-lg hover:shadow-xl' // Style when closed
                    } disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-150 ease-in-out`}
                    aria-expanded={isAddingAccount}
                    disabled={isLoadingAccounts || createAccountMutation.isPending || updateAccountMutation.isPending} // Disable during loading/mutation
                >
                    <span>
                    {isAddingAccount
                        ? <MinusIcon className="h-5 w-5 inline-block mr-2" />
                        : <PlusIcon className="h-5 w-5 inline-block mr-2" />
                    }
                    Add New Account
                    </span>
                    {isAddingAccount ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                </button>
                <AnimatePresence initial={false}>
                    {isAddingAccount && (
                        <motion.div
                            key="add-account-form"
                            variants={collapseVariants} // Use collapse variant
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="border-t border-white/20" // Add border when open
                        >                            <div className="p-4 md:p-6 bg-white/5 backdrop-blur-sm"> {/* Ensure background for form content */}
                                {/* Linear USDT Only Warning */}
                                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mb-4">
                                    <h4 className="text-yellow-400 font-semibold flex items-center">
                                        ⚠️ Linear USDT Futures Only
                                    </h4>
                                    <p className="text-yellow-200 text-sm mt-2">
                                        This system only supports <strong>Linear USDT Perpetual Futures</strong>. 
                                        Ensure your account has:
                                    </p>
                                    <ul className="text-yellow-200 text-xs mt-2 ml-4 space-y-1">
                                        <li>• Derivatives/Futures trading enabled</li>
                                        <li>• USDT balance for trading calculations</li>
                                        <li>• Read-only API permissions</li>
                                    </ul>
                                    <p className="text-yellow-200 text-xs mt-2">
                                        <strong>Note:</strong> Non-USDT assets (BTC, ETH, USDC, etc.) will be ignored by the system.
                                    </p>
                                </div>
                                
                                {/* Permanent Key Requirement */}
                                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 mb-4">
                                  <p className="text-purple-200 text-sm mb-2 font-semibold">
                                    ⏳ API Key Must Be Permanent (No Expiration)
                                  </p>
                                  <p className="text-purple-200 text-xs mb-2">
                                    When creating your API key:
                                  </p>
                                  <ul className="text-purple-200 text-xs ml-4 space-y-1">
                                    <li>• Select <strong>"No expiration"</strong> or <strong>"Never expires"</strong></li>
                                    <li>• Do NOT use 30, 60, or 90-day expiration</li>
                                    <li>• Keys with expiration dates will be rejected</li>
                                  </ul>
                                </div>
                                
                                <p className="text-sm text-white/70 mb-4">
                                    Please whitelist the following IP addresses in your exchange settings to allow Bot Blender read-only access:
                                    <br />
                                    <code className="bg-white/10 px-1 rounded text-white/90">18.156.158.53</code>, <code className="bg-white/10 px-1 rounded text-white/90">18.156.42.200</code>, <code className="bg-white/10 px-1 rounded text-white/90">52.59.103.54</code>, <code className="bg-white/10 px-1 rounded text-white/90">122.199.24.207</code>, <code className="bg-white/10 px-1 rounded text-white/90">13.228.225.19</code>, <code className="bg-white/10 px-1 rounded text-white/90">18.142.128.26</code>, <code className="bg-white/10 px-1 rounded text-white/90">54.254.162.138</code>
                                </p>
                                <form onSubmit={handleAccountSubmit(onAccountSubmit)} className="space-y-3">
                                    <FormInput label="Account Name" id="account_name" register={registerAccount} errors={accountErrors} required />
                                    
                                    {/* Exchange Selection Dropdown */}
                                    <div>
                                      <label htmlFor="exchange-select" className="block text-sm font-medium text-white/90 mb-1">Exchange</label>
                                      <Controller
                                        name="exchange_name"
                                        control={accountControl}
                                        rules={{ required: 'Exchange is required' }}
                                        defaultValue="bybit" // Default to Bybit
                                        render={({ field }) => (                                          <Select<ExchangeOption, false, GroupBase<ExchangeOption>>
                                            inputId="exchange-select"
                                            {...field}
                                            options={exchangeOptions}
                                            value={exchangeOptions.find(option => option.value === field.value) || null}
                                            onChange={selectedOption => field.onChange(selectedOption ? selectedOption.value : '')}
                                            classNames={selectClassNames} // Reuse existing select styles
                                            unstyled={true} // Remove all default react-select styles
                                            isSearchable={false} // Make non-searchable, only allow selection
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} // Render dropdown in document body to bypass container constraints
                                            styles={{
                                              menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                            }}
                                            instanceId="exchange-select-instance"
                                          />
                                        )}
                                      />
                                      {accountErrors.exchange_name && <p className="mt-1 text-xs text-red-400">{accountErrors.exchange_name.message}</p>}
                                    </div>

                                    <FormInput label="API Key" id="api_key" register={registerAccount} errors={accountErrors} required />
                                    <FormInput label="API Secret" id="api_secret" type="password" register={registerAccount} errors={accountErrors} required />
                                    
                                    {/* Conditional Passphrase Field for Bitget */}
                                    {watchAccountForm('exchange_name') === 'bitget' && (
                                      <div>
                                        <FormInput 
                                          label="API Passphrase" 
                                          id="passphrase" 
                                          type="password" 
                                          register={registerAccount} 
                                          errors={accountErrors} 
                                          required={true}
                                          placeholder="Your Bitget API passphrase"
                                        />
                                        <p className="mt-1 text-xs text-blue-300">
                                          🔑 Bitget requires an API passphrase. This is encrypted and stored securely.
                                        </p>
                                      </div>
                                    )}
                                    
                                    <div className="flex justify-end space-x-3 pt-3 border-t border-white/20">
                                        <button
                                            type="button"
                                            onClick={toggleAddAccountForm} // Use the toggle function to close
                                            className="px-3 py-1.5 text-sm border border-white/20 rounded-md text-white/80 hover:bg-white/10 cursor-pointer flex items-center"
                                        >
                                            <XMarkIcon className="w-4 h-4 mr-1" /> Cancel
                                        </button>                                        <button
                                            type="submit"
                                            disabled={isSubmittingAccount || createAccountMutation.isPending}
                                            className="px-3 py-1.5 text-sm rounded-md font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl flex items-center"
                                        >
                                            {isSubmittingAccount || createAccountMutation.isPending ? 'Linking...' : 'Link Account'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
             </div>
             {/* --- End Add New Account --- */}             {/* Existing Accounts List (Collapsible) */}
             <div className="border-b border-white/20 last:border-b-0"> {/* Remove bottom border on last item */}
                <button
                    onClick={toggleAccountsList}
                    className="w-full flex justify-between items-center p-4 text-left text-lg font-medium text-white/90 hover:bg-white/5 focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:ring-offset-1 cursor-pointer" // Added cursor-pointer and removed Standard style comment
                    aria-expanded={isAccountsExpanded}
                    disabled={isLoadingAccounts} // Disable only during initial load
                >
                    <span>
                    <ListBulletIcon className="h-5 w-5 inline-block mr-2" />
                    Existing Accounts ({isLoadingAccounts ? '...' : linkedAccounts?.length ?? 0})
                    </span>
                    {isAccountsExpanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                </button>
                <AnimatePresence initial={false}>
                    {isAccountsExpanded && (
                        <motion.div
                            key="accounts-list-content"
                            variants={listCollapseVariants} // Use list variant
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="border-t border-white/20" // Separator line
                        >                            {/* Accounts List Content */}
                            <div className="p-4 md:p-6 bg-white/5 backdrop-blur-sm"> {/* Fixed background for list content */}
                                {isLoadingAccounts ? (
                                    <div className="space-y-3">
                                        {[...Array(2)].map((_, i) => <SkeletonLoader key={i} className="h-10"/>)}
                                    </div>
                                ) : linkedAccounts && linkedAccounts.length > 0 ? (
                                    <ul className="space-y-3">
                                        {linkedAccounts.map(acc => (                                            <li key={acc.id} className="flex items-center justify-between p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-md">
                                                <div className="flex-1 min-w-0 mr-4">
                                                    <p className="text-sm font-medium text-white/90 truncate">{acc.account_name} ({acc.exchange_name})</p>
                                                    <div className="text-xs text-white/60 flex items-center">
                                                        API Key:
                                                        <span className="ml-1 font-mono">
                                                            {showApiKeyId === acc.id ? acc.api_key : `${acc.api_key.substring(0, 4)}...${acc.api_key.substring(acc.api_key.length - 4)}`}
                                                        </span>                                                        <button onClick={() => toggleApiKeyVisibility(acc.id)} className="ml-2 text-white/60 hover:text-white/90 cursor-pointer transition-colors">
                                                            {showApiKeyId === acc.id ? <EyeSlashIcon className="w-4 h-4"/> : <EyeIconOutline className="w-4 h-4"/>}
                                                        </button>
                                                        <button onClick={() => copyApiKey(acc.api_key)} className="ml-2 text-white/60 hover:text-white/90 cursor-pointer transition-colors">
                                                            <ClipboardDocumentIcon className="w-4 h-4"/>
                                                        </button>
                                                    </div>
                                                </div>                                                <div className="flex space-x-2">
                                                    <button onClick={() => openEditAccountModal(acc)} className="p-1 text-white/60 hover:text-blue-400 cursor-pointer transition-colors">
                                                        <PencilIcon className="w-4 h-4"/>
                                                    </button>
                                                    <button onClick={() => handleDeleteAccount(acc)} className="p-1 text-white/60 hover:text-red-400 cursor-pointer disabled:cursor-not-allowed transition-colors" disabled={deleteAccountMutation.isPending && deleteAccountMutation.variables === acc.id}>
                                                        <TrashIcon className="w-4 h-4"/>
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-white/60">No linked accounts yet.</p>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
             </div>             {/* --- End Existing Accounts List --- */}
          </motion.section>

          {/* Trading Reports Section */}
          <TradingReportsSection />

        </div>        {/* Right Column (Subscription) */}
        <div className="lg:col-span-1 space-y-8">           {/* Subscription Section */}
           <motion.section             variants={{
               hidden: { opacity: 0, y: 20 },
               visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeInOut" } }
             }}
             className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-xl overflow-hidden"
           >
             {/* Header with gradient background */}
             <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-6">
               <h2 className="text-xl font-semibold text-white flex items-center">
                 <CreditCardIcon className="w-6 h-6 mr-3"/> 
                 Subscription & Billing
               </h2>
               <p className="text-blue-100 text-sm mt-1">Manage your plan and billing preferences</p>
             </div>

             <div className="p-6">
               {isLoadingSubscription ? (
                 <div className="space-y-4">
                   <div className="animate-pulse">                     <div className="h-4 bg-white/20 rounded w-3/4 mb-3"></div>
                     <div className="h-4 bg-white/20 rounded w-1/2 mb-3"></div>
                     <div className="h-4 bg-white/20 rounded w-2/3 mb-6"></div>
                     <div className="h-10 bg-white/20 rounded w-48"></div>
                   </div>
                 </div>
               ) : subscriptionError ? (
                 <motion.div 
                   initial={{ opacity: 0, y: 10 }}
                   animate={{ opacity: 1, y: 0 }}
                   className="text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800"
                 >
                   <div className="flex items-center">
                     <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                       <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                     </svg>
                     Error loading subscription details
                   </div>
                   <p className="text-sm mt-1">{subscriptionError.message}</p>
                 </motion.div>
               ) : subscription && subscription.plan_type ? (
                 <motion.div 
                   initial={{ opacity: 0, y: 10 }}
                   animate={{ opacity: 1, y: 0 }}
                   className="space-y-6"
                 >
                   {/* Plan Info Card */}
                   <div className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                     <div className="flex items-center justify-between mb-3">
                       <div>
                         <h3 className="font-semibold text-lg text-white capitalize">
                           {subscription.plan_type} Plan
                         </h3>
                         <p className="text-sm text-white/70">
                           {subscription.plan_type === 'trial' ? 'Explore all features with your free trial' :
                            subscription.plan_type === 'pro' ? 'Full access to all premium features' :
                            subscription.plan_type === 'basic' ? 'Essential features for getting started' :
                            'Current subscription plan'}
                         </p>
                       </div>
                       <div className="text-right">                         <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                           subscription.status === 'active'
                             ? 'bg-green-500/20 text-green-300 border border-green-400/30'
                             : subscription.status === 'trialing'
                             ? 'bg-blue-500/20 text-blue-300 border border-blue-400/30'
                             : subscription.status === 'canceled'
                             ? 'bg-red-500/20 text-red-300 border border-red-400/30'
                             : 'bg-yellow-500/20 text-yellow-300 border border-yellow-400/30'
                         }`}>
                           <div className={`w-2 h-2 rounded-full mr-2 ${
                             subscription.status === 'active' ? 'bg-green-500' :
                             subscription.status === 'trialing' ? 'bg-blue-500' :
                             subscription.status === 'canceled' ? 'bg-red-500' : 'bg-yellow-500'
                           }`}></div>
                           {subscription.status || 'Unknown'}
                         </span>
                       </div>
                     </div>
                     
                     {/* Billing Info */}
                     <div className="flex items-center justify-between text-sm">
                       <span className="text-white/70">
                         {subscription.status === 'canceled' ? 'Access expires:' : 
                          subscription.status === 'trialing' ? 'Trial ends:' : 'Next billing:'}
                       </span>
                       <span className="font-medium text-white">
                         {subscription.current_period_end
                           ? formatUserDateTime(subscription.current_period_end, DATE_FORMAT_SHORT, 'N/A')
                           : 'N/A'}
                       </span>
                     </div>
                   </div>

                   {/* Actions */}
                   <div className="space-y-3">
                     <motion.button
                       whileHover={{ scale: 1.02 }}
                       whileTap={{ scale: 0.98 }}
                       onClick={handleManageSubscription}
                       disabled={portalMutation.isPending}
                       className="w-full inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-lg shadow-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                     >
                       {portalMutation.isPending ? (
                         <>
                           <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                           </svg>
                           Redirecting...
                         </>
                       ) : (
                         <>
                           Manage Payment Methods & Invoices
                           <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2" />
                         </>
                       )}
                     </motion.button>
                     
                     <div className="bg-blue-400/20 backdrop-blur-sm p-3 rounded-lg">
                       <p className="text-xs text-white/80 flex items-center">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                         </svg>
                         Securely manage payment methods, view invoices, download receipts through Stripe.
                       </p>
                     </div>
                   </div>
                   
                   {/* Plan Change Link */}
                   <div className="mt-6 pt-6 border-t border-white/20">
                     <p className="text-sm text-white/70 mb-3">
                       Want to upgrade or downgrade your subscription?
                     </p>
                     <motion.button
                       whileHover={{ scale: 1.02 }}
                       whileTap={{ scale: 0.98 }}
                       onClick={() => window.location.href = '/pricing'}
                       className="w-full inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-lg shadow-lg text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
                     >
                       Change Your Plan
                       <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2" />
                     </motion.button>
                   </div>
                 </motion.div>
               ) : (
                 <motion.div 
                   initial={{ opacity: 0, y: 10 }}
                   animate={{ opacity: 1, y: 0 }}
                   className="text-center py-8"
                 >                   <div className="bg-white/10 backdrop-blur-sm rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                     <CreditCardIcon className="w-8 h-8 text-white/60" />
                   </div>
                   <h3 className="text-lg font-medium text-white mb-2">
                     No Active Subscription
                   </h3>
                   <p className="text-sm text-white/70 mb-6">
                     Subscribe to unlock advanced trading analytics and portfolio management features.
                   </p>
                   <motion.button
                     whileHover={{ scale: 1.05 }}
                     whileTap={{ scale: 0.95 }}
                     onClick={() => window.location.href = '/pricing'}
                     className="inline-flex items-center px-6 py-3 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg transition-all duration-200"
                   >
                     View Plans & Pricing
                     <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                     </svg>
                   </motion.button>
                 </motion.div>
               )}
             </div>
           </motion.section>
        </div>      </div>

      {/* Portfolio Settings Section - Full Width */}
      <motion.section 
        layout 
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeInOut" } }
        }}
        className="mt-8 max-w-6xl mx-auto bg-white/10 backdrop-blur-lg border border-white/20 shadow-xl rounded-xl overflow-hidden"
      >
         {/* Section Header - Make entire header clickable */}
         <div 
            onClick={togglePortfolioSettings}
            className="p-4 md:p-6 border-b border-white/20 flex justify-between items-center cursor-pointer hover:bg-white/5 transition-colors duration-200"
            aria-expanded={isPortfolioExpanded}
         >            <h2 className="text-xl font-semibold text-white/90 flex items-center">
                <CogIcon className="w-5 h-5 mr-2 text-white/60"/>
                Portfolio Recommendation Engine
            </h2>
            {isPortfolioExpanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
         </div>

         <AnimatePresence initial={false}>
            {isPortfolioExpanded && (
                <motion.div
                    key="portfolio-settings"
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={collapseVariants}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                    <div className="p-4 md:p-6">
                        <PortfolioConfigCards
                            portfolioCategories={portfolioCategories}
                            onValueChange={handleConfigValueChange}
                            onRemoveOverride={handleRemoveConfigOverride}
                            onResetCategory={(category: string) => {
                                console.log('🔄 Resetting category:', category);
                                handleResetCategory(category);
                            }}
                            isLoadingCategories={isLoadingCategories}
                            isUpdating={updatePortfolioConfigMutation.isPending}
                        />
                    </div>
                </motion.div>
            )}
         </AnimatePresence>
      </motion.section>

      {/* --- Modals --- */}

      {/* Edit Linked Account Modal (Add modal removed) */}
      <Modal isOpen={isEditAccountModalOpen} onClose={() => { setEditAccountModalOpen(false); setEditingAccount(null); }} title={'Edit Linked Account'}>
          <form onSubmit={handleAccountSubmit(onAccountSubmit)} className="space-y-4">
              <FormInput label="Account Name" id="account_name" register={registerAccount} errors={accountErrors} required />              {/* Display Exchange Name (read-only for edit) */}
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">Exchange</label>
                <input
                    type="text"
                    value={editingAccount?.exchange_name || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-white/30 rounded-md shadow-sm bg-white/5 backdrop-blur-sm text-white/60 cursor-not-allowed"
                />
              </div>
              <FormInput label="API Key" id="api_key" register={registerAccount} errors={accountErrors} required />
              <FormInput label={`API Secret ${editingAccount ? '(Optional: Enter only to update)' : ''}`} id="api_secret" type="password" register={registerAccount} errors={accountErrors} required={!editingAccount} />              <div className="flex justify-end space-x-3 pt-4 border-t border-white/20">
                  {/* Update the onClick handler for the Cancel button */}
                  <button type="button" onClick={() => { setEditAccountModalOpen(false); setEditingAccount(null); }} className="px-4 py-2 text-sm border border-white/20 rounded-md text-white/80 hover:bg-white/10 cursor-pointer">Cancel</button>
                  <button
                    type="submit"
                    disabled={isSubmittingAccount || createAccountMutation.isPending || updateAccountMutation.isPending}
                    className="px-4 py-2 text-sm rounded-md font-semibold text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                      {isSubmittingAccount || createAccountMutation.isPending || updateAccountMutation.isPending ? 'Saving...' : (editingAccount ? 'Update Account' : 'Link Account')}
                  </button>              </div>
          </form>
      </Modal>

      {/* Delete Account Confirmation Modal */}
      <Modal 
        isOpen={isDeleteAccountModalOpen} 
        onClose={() => { setDeleteAccountModalOpen(false); setDeletingAccount(null); }} 
        title={'Delete Account'}
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
                <TrashIcon className="w-6 h-6 text-red-600" aria-hidden="true" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-white">
                Delete Account
              </h3>
              <div className="mt-2">
                <p className="text-sm text-white/70">
                  Are you sure you want to delete{' '}
                  <span className="font-semibold text-white">
                    {deletingAccount?.account_name}
                  </span>{' '}
                  ({deletingAccount?.exchange_name})? This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4 border-t border-white/20">
            <button 
              type="button" 
              onClick={() => { setDeleteAccountModalOpen(false); setDeletingAccount(null); }} 
              className="px-4 py-2 text-sm border border-white/20 rounded-md text-white/80 hover:bg-white/10 cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDeleteAccount}
              disabled={deleteAccountMutation.isPending}
              className="px-4 py-2 text-sm rounded-md font-semibold text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
        </div>
      </Modal>

      </motion.div>
    </div>
  );
};

export default Settings;